import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { useDispatchWindows, windowsActions } from "contexts/windows";
import { CORE_WINDOW_IDS } from "contexts/windows/constants";
import { coordinatesActions, selectCoordinates } from "features/coordinates";
import {
    measureActions,
    selectMeasureEntities,
    selectMeasureVisible,
    selectObjectMeasure,
} from "features/measure/measureSlice";
import { clippingOutlineLaserActions, selectOutlineLasers } from "features/outlineLaser/outlineLaserSlice";
import { pointLineActions, selectPointLines } from "features/pointLine/pointLineSlice";
import { renderActions, selectClippingPlanes } from "features/render";
import {
    selectPlainIsolationActive,
    selectPlainIsolationPaused,
    selectPlainIsolationSnapshotIds,
    selectPreIsolationVisibility,
} from "features/render/renderSlice";
import { ObjectVisibility } from "features/render/types";
import { cloneClippingPlanes } from "features/sectionPlane/sectionPlaneUndoRedo";
import { useEnterToolMode } from "features/toolMode/useEnterToolMode";
import { undoRedoActions } from "features/undoRedo";
import { collectPdfHiddenIds, partitionHiddenIds, useObjectTreeHidden } from "hooks/useObjectTreeHidden";
import { ProcoreToolMode } from "slices/procoreUx";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

export type ModificationCategory = {
    id: string;
    label: string;
    count: number;
    showCount: boolean;
    isEnabled: boolean;
    onToggle: (() => void) | undefined;
    onClear: () => void;
    onActivate: () => void;
};

/**
 * Returns the currently active modification categories and actions to toggle or clear each one.
 * A category is included only when it has at least one active modification.
 */
export function useViewModificationCategories(): ModificationCategory[] {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const dispatchHidden = useDispatchHidden();
    const dispatchWindows = useDispatchWindows();
    const enterToolMode = useEnterToolMode();

    // Objects Hidden — PDFs/2D sheets stay hidden in 3D, but are not counted here.
    const hidden = useHidden();
    const { objectTreeHiddenCount, objectTreeSnapshotCount, pdfObjectIds } = useObjectTreeHidden();
    const hiddenSnapshot = hidden.toggleSnapshot;
    const hiddenCount = objectTreeHiddenCount;
    const plainIsolationActive = useAppSelector(selectPlainIsolationActive);
    const plainIsolationPaused = useAppSelector(selectPlainIsolationPaused);
    const preIsolationVisibility = useAppSelector(selectPreIsolationVisibility);
    const plainIsolationSnapshotIds = useAppSelector(selectPlainIsolationSnapshotIds);
    // Category stays visible even when toggled off (snapshot has items but idArr is empty).
    // Isolation is also included here: "Isolate is just a way to bulk apply hide to objects."
    const isHiddenCategoryActive =
        hiddenCount > 0 || objectTreeSnapshotCount > 0 || plainIsolationActive || plainIsolationPaused;
    // Sectioning
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const sectionCount = clippingPlanes.planes.length;
    const isSectioningActive = sectionCount > 0;

    // Measurements — aggregate all measurement types
    const coordinates = useAppSelector(selectCoordinates);
    const lasers = useAppSelector(selectOutlineLasers);
    const pointLines = useAppSelector(selectPointLines);
    const measureEntities = useAppSelector(selectMeasureEntities);
    const measureVisible = useAppSelector(selectMeasureVisible);
    const objectMeasure = useAppSelector(selectObjectMeasure);

    const coordinatesCount = coordinates.length;
    const lasersCount = lasers.length;
    const pointLinesCount = pointLines.filter((pl) => pl.points.length > 0).length;
    const dimensionsCount = measureEntities.filter((set) => set.length > 0).length;
    // Object-to-object is at most one measurement, and only counts once it has a result — a single
    // pick, or a pair the API found no connection between, has nothing drawn in the view.
    const objectMeasureCount = objectMeasure.result ? 1 : 0;
    const measurementCount = coordinatesCount + lasersCount + pointLinesCount + dimensionsCount + objectMeasureCount;
    const isMeasurementsActive = measurementCount > 0;

    const handleToggleHidden = useCallback(() => {
        // Isolation-only toggle: pause/resume without touching eye-hidden state.
        // Does not need pdfObjectIds — no PDF partitioning involved.
        if (hiddenCount === 0 && objectTreeSnapshotCount === 0) {
            if (plainIsolationActive) {
                dispatch(renderActions.pausePlainIsolation());
                dispatch(
                    undoRedoActions.push({
                        description: "Pause isolation",
                        undoCallback: () => dispatch(renderActions.resumePlainIsolation()),
                        redoCallback: () => dispatch(renderActions.pausePlainIsolation()),
                    }),
                );
            } else if (plainIsolationPaused) {
                dispatch(renderActions.resumePlainIsolation());
                dispatch(
                    undoRedoActions.push({
                        description: "Resume isolation",
                        undoCallback: () => dispatch(renderActions.pausePlainIsolation()),
                        redoCallback: () => dispatch(renderActions.resumePlainIsolation()),
                    }),
                );
            }
            return;
        }

        if (!pdfObjectIds) {
            return;
        }

        const beforeIds = snapshotUndoIds(hidden.ids);
        const beforeSnapshot = snapshotUndoIds(hiddenSnapshot);
        const wasIsolationActive = plainIsolationActive;

        const { objectTreeIds, pdfIds } = partitionHiddenIds(hidden.ids, pdfObjectIds);
        if (hiddenCount > 0) {
            dispatchHidden(hiddenActions.setToggleSnapshot(objectTreeIds));
            dispatchHidden(hiddenActions.setIds(pdfIds));
            if (wasIsolationActive) {
                dispatch(renderActions.pausePlainIsolation());
            }
            dispatch(
                undoRedoActions.push({
                    description: "Toggle hidden objects off",
                    undoCallback: () => {
                        dispatchHidden(hiddenActions.setToggleSnapshot(restoreUndoIds(beforeSnapshot)));
                        dispatchHidden(hiddenActions.setIds(restoreUndoIds(beforeIds)));
                        if (wasIsolationActive) {
                            dispatch(renderActions.resumePlainIsolation());
                        }
                    },
                    redoCallback: () => {
                        const restored = partitionHiddenIds(restoreUndoIds(beforeIds), pdfObjectIds);
                        dispatchHidden(hiddenActions.setToggleSnapshot(restored.objectTreeIds));
                        dispatchHidden(hiddenActions.setIds(restored.pdfIds));
                        if (wasIsolationActive) {
                            dispatch(renderActions.pausePlainIsolation());
                        }
                    },
                }),
            );
        } else {
            const { objectTreeIds: snapshotObjectTreeIds } = partitionHiddenIds(hiddenSnapshot, pdfObjectIds);
            dispatchHidden(hiddenActions.add(snapshotObjectTreeIds));
            dispatch(
                undoRedoActions.push({
                    description: "Toggle hidden objects on",
                    undoCallback: () => {
                        dispatchHidden(hiddenActions.setToggleSnapshot(restoreUndoIds(beforeSnapshot)));
                        dispatchHidden(hiddenActions.setIds(restoreUndoIds(beforeIds)));
                    },
                    redoCallback: () => {
                        dispatchHidden(hiddenActions.add(snapshotObjectTreeIds));
                    },
                }),
            );
        }
    }, [
        pdfObjectIds,
        hidden.ids,
        hiddenSnapshot,
        hiddenCount,
        objectTreeSnapshotCount,
        plainIsolationActive,
        plainIsolationPaused,
        dispatchHidden,
        dispatch,
    ]);

    const handleClearHidden = useCallback(() => {
        // Isolation-only clear — no eye-hidden objects, no snapshot — does not need PDF partitioning.
        if (hiddenCount === 0 && objectTreeSnapshotCount === 0) {
            const wasIsolationActive = plainIsolationActive;
            const wasIsolationPaused = plainIsolationPaused;
            const capturedPreIsolation = preIsolationVisibility;
            const capturedSnapshotIds = plainIsolationSnapshotIds ? [...plainIsolationSnapshotIds] : undefined;

            dispatch(renderActions.setPlainIsolationActive({ active: false }));
            dispatch(renderActions.clearPreIsolationVisibility());
            dispatch(renderActions.setDefaultVisibility(capturedPreIsolation ?? ObjectVisibility.Neutral));

            dispatch(
                undoRedoActions.push({
                    description: "Clear isolation",
                    undoCallback: () => {
                        if (capturedPreIsolation !== undefined) {
                            dispatch(renderActions.setPreIsolationVisibility(capturedPreIsolation));
                            if (wasIsolationActive) {
                                dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
                                dispatch(
                                    renderActions.setPlainIsolationActive({
                                        active: true,
                                        snapshotIds: capturedSnapshotIds,
                                    }),
                                );
                            } else if (wasIsolationPaused) {
                                dispatch(renderActions.pausePlainIsolation());
                                dispatch(renderActions.setPlainIsolationSnapshotIds(capturedSnapshotIds ?? []));
                            }
                        }
                    },
                    redoCallback: () => {
                        dispatch(renderActions.setPlainIsolationActive({ active: false }));
                        dispatch(renderActions.clearPreIsolationVisibility());
                        dispatch(renderActions.setDefaultVisibility(capturedPreIsolation ?? ObjectVisibility.Neutral));
                    },
                }),
            );
            return;
        }

        if (!pdfObjectIds) {
            return;
        }
        const pdfIds = collectPdfHiddenIds(hidden.ids, pdfObjectIds);
        const pdfSnapshotIds = collectPdfHiddenIds(hiddenSnapshot, pdfObjectIds);
        const beforeIds = snapshotUndoIds(hidden.ids);
        const beforeSnapshot = snapshotUndoIds(hiddenSnapshot);

        // Snapshot isolation state before clearing so undo can restore it.
        // Capture both active and paused — clearing while paused must also clean up.
        const wasIsolationActive = plainIsolationActive;
        const wasIsolationPaused = plainIsolationPaused;
        const hadIsolation = wasIsolationActive || wasIsolationPaused;
        const capturedPreIsolation = preIsolationVisibility;
        const capturedSnapshotIds = plainIsolationSnapshotIds ? [...plainIsolationSnapshotIds] : undefined;

        dispatchHidden(hiddenActions.setToggleSnapshot(pdfSnapshotIds));
        dispatchHidden(hiddenActions.setIds(pdfIds));

        if (hadIsolation) {
            dispatch(renderActions.setPlainIsolationActive({ active: false }));
            dispatch(renderActions.clearPreIsolationVisibility());
            dispatch(renderActions.setDefaultVisibility(capturedPreIsolation ?? ObjectVisibility.Neutral));
        }

        dispatch(
            undoRedoActions.push({
                description: "Clear hidden objects",
                undoCallback: () => {
                    dispatchHidden(hiddenActions.setToggleSnapshot(restoreUndoIds(beforeSnapshot)));
                    dispatchHidden(hiddenActions.setIds(restoreUndoIds(beforeIds)));
                    if (hadIsolation && capturedPreIsolation !== undefined) {
                        dispatch(renderActions.setPreIsolationVisibility(capturedPreIsolation));
                        if (wasIsolationActive) {
                            dispatch(renderActions.setDefaultVisibility(ObjectVisibility.Transparent));
                            dispatch(
                                renderActions.setPlainIsolationActive({
                                    active: true,
                                    snapshotIds: capturedSnapshotIds,
                                }),
                            );
                        } else {
                            // Was paused — restore to paused state
                            dispatch(renderActions.pausePlainIsolation());
                            dispatch(renderActions.setPlainIsolationSnapshotIds(capturedSnapshotIds ?? []));
                        }
                    }
                },
                redoCallback: () => {
                    dispatchHidden(
                        hiddenActions.setToggleSnapshot(
                            collectPdfHiddenIds(restoreUndoIds(beforeSnapshot), pdfObjectIds),
                        ),
                    );
                    dispatchHidden(hiddenActions.setIds(collectPdfHiddenIds(restoreUndoIds(beforeIds), pdfObjectIds)));
                    if (hadIsolation) {
                        dispatch(renderActions.setPlainIsolationActive({ active: false }));
                        dispatch(renderActions.clearPreIsolationVisibility());
                        dispatch(renderActions.setDefaultVisibility(capturedPreIsolation ?? ObjectVisibility.Neutral));
                    }
                },
            }),
        );
    }, [
        hiddenCount,
        objectTreeSnapshotCount,
        pdfObjectIds,
        hidden.ids,
        hiddenSnapshot,
        dispatchHidden,
        dispatch,
        plainIsolationActive,
        plainIsolationPaused,
        preIsolationVisibility,
        plainIsolationSnapshotIds,
    ]);

    const handleToggleSectioning = useCallback(() => {
        const before = { planes: cloneClippingPlanes(clippingPlanes.planes), enabled: clippingPlanes.enabled };
        dispatch(
            renderActions.setClippingPlanes({
                ...clippingPlanes,
                enabled: !clippingPlanes.enabled,
            }),
        );
        dispatch(
            undoRedoActions.push({
                description: "Toggle sectioning",
                undoCallback: () => dispatch(renderActions.setClippingPlanes(before)),
                redoCallback: () => dispatch(renderActions.setClippingPlanes({ ...before, enabled: !before.enabled })),
            }),
        );
    }, [dispatch, clippingPlanes]);

    const handleClearSectioning = useCallback(() => {
        const before = { planes: cloneClippingPlanes(clippingPlanes.planes), enabled: clippingPlanes.enabled };
        dispatch(renderActions.setClippingPlanes({ planes: [], enabled: false }));
        dispatch(
            undoRedoActions.push({
                description: "Clear sectioning",
                undoCallback: () => dispatch(renderActions.setClippingPlanes(before)),
                redoCallback: () => dispatch(renderActions.setClippingPlanes({ planes: [], enabled: false })),
            }),
        );
    }, [dispatch, clippingPlanes]);

    const handleToggleMeasurements = useCallback(() => {
        const before = measureVisible;
        dispatch(measureActions.setVisible(!measureVisible));
        dispatch(
            undoRedoActions.push({
                description: "Toggle measurements",
                undoCallback: () => dispatch(measureActions.setVisible(before)),
                redoCallback: () => dispatch(measureActions.setVisible(!before)),
            }),
        );
    }, [dispatch, measureVisible]);

    const handleClearMeasurements = useCallback(() => {
        const beforeCoordinates = coordinates;
        const beforeLasers = lasers;
        const beforePointLines = pointLines;
        const beforeMeasureEntities = measureEntities.map((set) => structuredClone(set));
        // `measureActions.clear()` drops this too, so undo has to put it back explicitly.
        const beforeObjectMeasure = structuredClone(objectMeasure);
        dispatch(coordinatesActions.clear());
        dispatch(clippingOutlineLaserActions.clear());
        dispatch(pointLineActions.clear());
        dispatch(measureActions.clear());
        dispatch(
            undoRedoActions.push({
                description: "Clear measurements",
                undoCallback: () => {
                    dispatch(coordinatesActions.setCoordinates(beforeCoordinates));
                    dispatch(clippingOutlineLaserActions.setLasers(beforeLasers));
                    dispatch(pointLineActions.setPointLines(beforePointLines));
                    if (beforeMeasureEntities.length === 1) {
                        dispatch(measureActions.setSelectedEntities(beforeMeasureEntities[0]));
                    } else {
                        beforeMeasureEntities.forEach((entities, i) =>
                            dispatch(measureActions.setMeasureSet({ idx: i, entities })),
                        );
                    }
                    dispatch(measureActions.setObjectMeasure(beforeObjectMeasure));
                },
                redoCallback: () => {
                    dispatch(coordinatesActions.clear());
                    dispatch(clippingOutlineLaserActions.clear());
                    dispatch(pointLineActions.clear());
                    dispatch(measureActions.clear());
                },
            }),
        );
    }, [dispatch, coordinates, lasers, pointLines, measureEntities, objectMeasure]);

    const handleActivateObjectTree = useCallback(() => {
        dispatchWindows(windowsActions.toggleWindow(CORE_WINDOW_IDS.OBJECT_TREE));
    }, [dispatchWindows]);

    const handleActivateSection = useCallback(() => {
        enterToolMode({ mode: ProcoreToolMode.Section });
    }, [enterToolMode]);

    const handleActivateMeasure = useCallback(() => {
        enterToolMode({ mode: ProcoreToolMode.Measure });
    }, [enterToolMode]);

    return useMemo(() => {
        const categories: ModificationCategory[] = [];

        if (isHiddenCategoryActive) {
            const isolationOnly = hiddenCount === 0 && objectTreeSnapshotCount === 0;
            categories.push({
                id: "objects-hidden",
                label: isolationOnly ? t("viewModifications.objectsIsolated") : t("viewModifications.objectsHidden"),
                count: isolationOnly
                    ? (plainIsolationSnapshotIds?.length ?? 0)
                    : hiddenCount > 0
                      ? hiddenCount
                      : objectTreeSnapshotCount,
                showCount: true,
                isEnabled: isolationOnly ? plainIsolationActive : hiddenCount > 0 || plainIsolationActive,
                onToggle: handleToggleHidden,
                onClear: handleClearHidden,
                onActivate: handleActivateObjectTree,
            });
        }

        if (isSectioningActive) {
            categories.push({
                id: "sectioning",
                label: t("viewModifications.sectioning"),
                count: sectionCount,
                showCount: false,
                isEnabled: clippingPlanes.enabled,
                onToggle: handleToggleSectioning,
                onClear: handleClearSectioning,
                onActivate: handleActivateSection,
            });
        }

        if (isMeasurementsActive) {
            categories.push({
                id: "measurements",
                label: t("viewModifications.measurements"),
                count: measurementCount,
                showCount: false,
                isEnabled: measureVisible,
                onToggle: handleToggleMeasurements,
                onClear: handleClearMeasurements,
                onActivate: handleActivateMeasure,
            });
        }

        return categories;
    }, [
        isHiddenCategoryActive,
        hiddenCount,
        objectTreeSnapshotCount,
        plainIsolationActive,
        plainIsolationSnapshotIds,
        handleToggleHidden,
        handleClearHidden,
        handleActivateObjectTree,
        isSectioningActive,
        sectionCount,
        clippingPlanes.enabled,
        handleToggleSectioning,
        handleClearSectioning,
        handleActivateSection,
        isMeasurementsActive,
        measurementCount,
        measureVisible,
        handleToggleMeasurements,
        handleClearMeasurements,
        handleActivateMeasure,
        t,
    ]);
}
